import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'

// Where Floating UI put the reference, in the same coordinates as the floating element's.
const REFERENCE_POSITION = {
  name: 'referencePosition',
  fn: ({ rects }) => ({ data: { x: rects.reference.x, y: rects.reference.y } })
}

// Keeps `floating` next to `reference` with `position: fixed`, which takes it
// out of the overflow clipping of every scroller around the reference — a
// modal body, a table — and follows the reference as they scroll, until stopped.
//
//   const float = floatFixed(field, list, { matchWidth: true })
//   float.update() // the field moved in a way Floating UI cannot see
//   float.stop()   // the list closed
//
// `middleware` replaces the default offset, flip and shift.
export function floatFixed (reference, floating, { placement = 'bottom-start', gap = 4, middleware = null, matchWidth = false } = {}) {
  let active = true
  let latest = 0

  const update = async () => {
    const call = ++latest

    // Before measuring: Floating UI measures an absolute element from its
    // offset parent — for an Easepick calendar, the wrapper beside the field.
    floating.style.position = 'fixed'
    if (matchWidth) floating.style.width = `${reference.offsetWidth}px`

    const { x, y, middlewareData } = await computePosition(reference, floating, {
      strategy: 'fixed',
      placement,
      middleware: [...(middleware || [offset(gap), flip({ padding: 8 }), shift({ padding: 8 })]), REFERENCE_POSITION]
    })
    // Stopped while Floating UI was measuring — the caller has cleared these
    // styles — or overtaken by a later call, whose answer is the newer one: an
    // older placement can hold a different flip or shift.
    if (!active || call !== latest) return

    Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
    landAt(reference, floating, { x, y }, middlewareData.referencePosition)
  }

  const stopAutoUpdate = autoUpdate(reference, floating, update)

  return {
    update,
    stop () {
      active = false
      stopAutoUpdate()
    }
  }
}

// A fixed element is placed against its containing block, which Floating UI
// works out — but in WebKit it takes `backdrop-filter` and `filter` for not
// making one, and Safari now does: inside the blurred `.header-sticky`, a
// dropdown landed off by the header's own offset. Floating UI's coordinates
// are relative to whichever box it settled on, so what is checked is the
// offset from the reference, the same in any box, and the element is moved by
// however far that missed.
function landAt (reference, floating, placed, referencePosition) {
  const floatingRect = floating.getBoundingClientRect()
  const referenceRect = reference.getBoundingClientRect()
  const missX = (floatingRect.left - referenceRect.left) - (placed.x - referencePosition.x)
  const missY = (floatingRect.top - referenceRect.top) - (placed.y - referencePosition.y)
  if (Math.abs(missX) < 1 && Math.abs(missY) < 1) return

  Object.assign(floating.style, { left: `${placed.x - missX}px`, top: `${placed.y - missY}px` })
}
